import { Button } from "@/design-system/Button/Button";
import { ChemistryIcon } from "@/design-system/Chemistry/Chemistry";
import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import { cn } from "@/lib/utils";
import {
  clearSbcParams,
  filtersActiveSelector,
} from "@/redux/sbcs/sbcFiltersSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import { Chemistry } from "@repo/shared/types";
import { type ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

const STEP_MS = 420;

/**
 * The wait, told in the game's own language: chemistry building up from
 * empty, one diamond at a time, then starting over while the solver works, and the line beneath catching a
 * light as it passes. Still under reduced motion.
 */
export const Solving = ({
  label,
  textClassName,
}: {
  /** Defaults to the solver's line; the squad builder passes its own. */
  label?: string;
  textClassName?: string;
}) => {
  const { t } = useTranslation("sbcSolution");
  const stillMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [chemistry, setChemistry] = useState<Chemistry>(0);

  useEffect(() => {
    if (stillMotion) return;
    const id = setInterval(
      () => setChemistry((c) => ((c + 1) % 4) as Chemistry),
      STEP_MS,
    );
    return () => clearInterval(id);
  }, [stillMotion]);

  return (
    <div className="flex items-center gap-3">
      <ChemistryIcon
        chemistry={stillMotion ? 3 : chemistry}
        className="w-7 shrink-0"
      />
      <span
        className={cn(
          "font-semibold",
          stillMotion
            ? "text-white"
            : "animate-solve-shimmer bg-clip-text text-transparent",
          textClassName ?? "whitespace-nowrap text-sm sm:text-base",
        )}
        style={
          stillMotion
            ? undefined
            : {
                backgroundImage:
                  "linear-gradient(90deg, var(--color-neutral-400) 0%, var(--color-neutral-50) 50%, var(--color-neutral-400) 100%)",
                backgroundSize: "200% 100%",
              }
        }
      >
        {label ?? t("generating")}
      </span>
    </div>
  );
};

const NoSolution = ({
  error,
  onSolveAgain,
}: {
  error?: string;
  onSolveAgain: () => void;
}) => {
  const { t } = useTranslation(["sbcSolution", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const hasFilters = useSelector(filtersActiveSelector);

  return (
    <div className="flex flex-col items-center gap-3 px-6 text-center">
      <span className="text-base font-bold text-white">{t("noSolution")}</span>
      {error && (
        <p className="max-w-80 rounded-lg bg-neutral-800 px-3 py-2 text-sm text-neutral-300">
          {error}
        </p>
      )}
      <div className="flex gap-2 pt-1">
        <Button variant="primary" size="small" onClick={onSolveAgain}>
          {t("solveAgain")}
        </Button>
        <Button
          variant="secondary"
          size="small"
          disabled={!hasFilters}
          onClick={() => dispatch(clearSbcParams())}
        >
          {hasFilters ? t("common:clearFilters") : t("noFilters")}
        </Button>
      </div>
    </div>
  );
};

/** The floating pill both the solver and the squad builder report in. */
export const StatusPanel = ({ children }: { children: ReactNode }) => (
  <div className="relative animate-fade-in rounded-2xl border border-neutral-700 bg-panel-base/95 px-5 py-4 shadow-[0_18px_44px_rgba(0,0,0,0.55)] backdrop-blur-sm">
    {children}
  </div>
);

/**
 * What the solver is doing, floated over the pitch. Positioned by whoever
 * renders it (PitchView centres it on the squad), so the panel only styles
 * itself.
 */
export const SolveStatus = ({
  status,
  error,
  onSolveAgain,
  onClose,
}: {
  status: "pending" | "rejected";
  error?: string;
  onSolveAgain: () => void;
  /** Dismisses the failure panel; the wait cannot be dismissed. */
  onClose?: () => void;
}) => (
  <StatusPanel>
    {status === "pending" ? (
      <Solving />
    ) : (
      <>
        {onClose && (
          <div className="absolute right-2 top-2">
            <CloseButton onClick={onClose} />
          </div>
        )}
        <NoSolution error={error} onSolveAgain={onSolveAgain} />
      </>
    )}
  </StatusPanel>
);
